import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  BookingOverviewPage,
  BookingFilter,
  bookingPeriods,
} from "./BookingOverviewPage";

import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useTimeoutManager } from "@greeter-venue/utility/Hooks";
import { format, secondsToMilliseconds } from "date-fns";

import { DatePeriod } from "@greeter/date";
import {
  chunk,
  flatten,
  isEmpty,
  orderBy,
  partial,
  uniq,
  uniqBy,
} from "lodash";
import { useBookingsQuery, useVenueQuery } from "@greeter-venue/api/Hooks";
import { useAuth, useGreeterVenueApi } from "@greeter/matter";
import { bookingKeys } from "@greeter-venue/api/QueryKeys";
import { Booking } from "@greeter/core";
import { useIonViewDidEnter } from "@ionic/react";

export type BookingOverviewApiHandlerProps = {};

const log = partial(console.log, "[BookingOverviewApiHandler]");

const formatDate = (date: Date) => format(date, "yyyy-MM-dd");

export function BookingOverviewApiHandler(
  props: BookingOverviewApiHandlerProps
) {
  const { api: venueApi } = useGreeterVenueApi();

  const [addTimeout] = useTimeoutManager();

  const [filter, setFilter] = useState<BookingFilter>("all upcoming");
  const [{ from, to }, setTimeSpan] = useState<DatePeriod>(
    bookingPeriods()[filter]
  );
  const handleFilterChange = useCallback(
    (newFilter: BookingFilter) => {
      const periods = bookingPeriods();

      if (newFilter !== filter) {
        setFilter(newFilter);
        setTimeSpan(periods[newFilter]);
      }
    },
    [filter]
  );

  useEffect(() => {
    handleFilterChange(filter);
  }, [filter, handleFilterChange]);

  const [hasMore, setHasMore] = useState(true);

  // useEffect(() => {
  //   // TODO: Key the orders on a filter prefix basis?
  //   // TODO: How about paging. Does that still work?
  //   queryClient.invalidateQueries({ queryKey });
  // }, [filter, queryClient, queryKey]);

  const venue = useVenueQuery();
  const bookingsQuery = useBookingsQuery({
    period: new DatePeriod({ from, to }),
    venueId: venue.data?.id,
  });
  useIonViewDidEnter(() => void bookingsQuery.refetch());

  // TODO: Reintroduce paging when necessary
  // const bookingsQuery = useInfiniteQuery({
  //   queryKey: [
  //     venue.data?.id,
  //     ...bookingKeys.all(new DatePeriod({ from, to })),
  //   ],
  //   initialPageParam: 0,
  //   async queryFn({ pageParam = 0 }) {
  //     const bookings = await venueApi.fetchBookings({
  //       page: pageParam,
  //       pageSize: 10,
  //       period: new DatePeriod({ from, to }),
  //     });

  //     if (hasMore && isEmpty(bookings)) setHasMore(false);

  //     return bookings;
  //   },
  //   getNextPageParam: (_lastPage, pages) =>
  //     hasMore ? pages.length : undefined,
  //   staleTime: secondsToMilliseconds(30),
  // });

  const invalidateBookingData = useCallback(
    (timeout = 1000) =>
      void addTimeout(() => {
        log(`Invalidating queries`);
        bookingsQuery.refetch();
      }, timeout),
    [addTimeout, bookingsQuery]
  );

  const acceptBookingMutation = useMutation({
    mutationFn: ({ bookingId }: { bookingId: string }) =>
      venueApi.acceptBooking(bookingId),
    onSuccess: () => invalidateBookingData(),
  });

  const rejectBookingMutation = useMutation({
    mutationFn: venueApi.rejectBooking.bind(venueApi),
    onSuccess: () => invalidateBookingData(),
  });

  const beginBookingMutation = useMutation({
    mutationFn: venueApi.beginBooking.bind(venueApi),
    onSuccess: () => invalidateBookingData(),
  });

  // TODO: This will turn in the a problem in the future when
  // bookings grow large. We might be DOSsing the backend here.
  // A delay is introduced to stave off the issue for now.
  const customersQuery = useQueries({
    queries: chunk(
      bookingsQuery.data
        ?.sort((a, b) => Number(b.period.from) - Number(a.period.from))
        .map((b) => b.customerId) ?? [],
      10
    ).map((chunk, index) => ({
      queryKey: ["customers", ...uniq(chunk).sort()],
      queryFn: async () => {
        await new Promise((resolve) => setTimeout(resolve, index * 200));
        return await venueApi.fetchCustomers(chunk);
      },
      staleTime: Infinity, // Or a suitable value, depending on customer data freshness
      cacheTime: Infinity, // Or a suitable value
      enabled: chunk.length > 0,
    })),

    combine(result) {
      return (
        flatten(result.filter((q) => !!q.data).map((q) => q.data ?? [])) ?? []
      );
    },
  });

  // const customersQuery = useInfiniteQuery({
  //   queryKey: ["customers", ...orderBy(uniq(flatten(bookingsQuery)), (g) => g)],
  // });
  // const customersQuery = useQuery({
  //   queryKey: [
  //     "customers",
  //     ...uniq(flatten(bookingsQuery.data?.pages).map((b) => b.customerId)),
  //   ],
  //   queryFn: async () => {
  //     if (bookingsQuery.data)
  //       return await venueApi.fetchCustomers(
  //         flatten(bookingsQuery.data).map((b) => b.customerId)
  //       );
  //   },
  //   enabled: bookingsQuery.data && bookingsQuery.data?.length > 0,
  // });

  const endBookingMutation = useMutation({
    mutationFn: venueApi.endBooking.bind(venueApi),
    onSuccess: () => invalidateBookingData(),
  });

  // const handleEndReached = useCallback(() => {
  //   if (hasMore) {
  //     log("End reached, fetching new page!");
  //     fetchBookingsQuery.fetchNextPage();
  //   } else {
  //     log("End reached, no more pages.");
  //   }
  // }, []);

  const handleRefresh = useCallback(async () => {
    await bookingsQuery.refetch();
  }, [bookingsQuery]);

  const handleBegin = useCallback(
    async (booking: Booking) => {
      return await beginBookingMutation.mutateAsync(booking.id);
    },
    [beginBookingMutation]
  );

  const handleReject = useCallback(
    async (booking: Booking, message: string) => {
      return await rejectBookingMutation.mutateAsync({
        bookingId: booking.id,
        message: message,
      });
    },
    [rejectBookingMutation]
  );

  const handleAccept = useCallback(
    async (booking: Booking) => {
      return await acceptBookingMutation.mutateAsync({
        bookingId: booking.id,
      });
    },
    [acceptBookingMutation]
  );

  const handleEnd = useCallback(
    async (booking: Booking) => {
      return await endBookingMutation.mutateAsync(booking.id);
    },
    [endBookingMutation]
  );

  const bookings = useMemo(() => {
    return uniqBy(
      (bookingsQuery.data ?? []).sort((b) => b.createdAt.getTime()),
      (b) => b.id
    );
  }, [bookingsQuery.data]);

  const period = useMemo(() => {
    return new DatePeriod({ from, to });
  }, [from, to]);

  const venueQuery = useVenueQuery();
  const { userInfo } = useAuth();

  return (
    <BookingOverviewPage
      onAccept={handleAccept}
      onReject={handleReject}
      onBegin={handleBegin}
      onEnd={handleEnd}
      onRefresh={handleRefresh}
      bookings={bookings}
      customers={customersQuery}
      onFilterChange={handleFilterChange}
      hasMore={hasMore}
      period={period}
      venue={venueQuery.data}
      venueAdminName={userInfo?.name ?? ""}
    />
  );
}
